/**
 * Sprint 17 B1: A/B Split Node — visual branch indicator with variant percentages
 */
import { useTranslation } from "@/i18n";
import { cn } from "@/lib/utils";
import { Split } from "lucide-react";
import type { SplitNodeData } from "./types";

interface SplitNodeProps {
  node: SplitNodeData;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

export function SplitNode({ node, isSelected, onSelect }: SplitNodeProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={() => onSelect(node.id)}
      className={cn(
        "flex flex-col gap-1 rounded-lg border-2 px-4 py-3 text-left shadow-sm transition-colors min-w-[200px]",
        "bg-[var(--color-accent-action)]/5 border-[var(--color-accent-action)]/20 hover:border-[var(--color-accent-action)]/50",
        isSelected && "ring-2 ring-[var(--color-accent-action)] border-[var(--color-accent-action)]/80",
      )}
    >
      <div className="flex items-center gap-2">
        <Split className="h-4 w-4 text-[var(--color-accent-action)]" />
        <span className="text-xs font-medium text-[var(--color-accent-action)] uppercase">
          {t("journey.node.split")}
        </span>
      </div>
      <div className="flex gap-2 mt-1">
        {node.variants.map((v) => (
          <span
            key={v.id}
            className="text-xs bg-[var(--color-accent-action)]/10 text-[var(--color-accent-action)] rounded px-2 py-0.5"
          >
            {v.name}: {v.percentage}%
          </span>
        ))}
      </div>
    </button>
  );
}
